import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, Button, Stack, Chip, IconButton, CircularProgress } from '@mui/joy';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetPendingWriteOffRequests, APIReviewWriteOffRequest, parseApiError,
} from '../../api';

const fmtQty = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
};
const fmtDateTime = (iso) => {
    try {
        return new Date(iso).toLocaleString('ru-RU', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });
    } catch { return ''; }
};

export default function WriteOffRequestsPage() {
    const { token, showToast } = useContext(AppContext);
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await APIGetPendingWriteOffRequests(token);
        setList(Array.isArray(res) ? res : []);
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const review = async (req, action) => {
        let comment = '';
        if (action === 'reject') comment = window.prompt('Причина отклонения (необязательно):') || '';
        setBusyId(req.id);
        const res = await APIReviewWriteOffRequest(token, req.id, { action, comment });
        setBusyId(null);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось'), 'danger'); return; }
        showToast(action === 'approve' ? 'Списание одобрено ✓' : 'Заявка отклонена',
            action === 'approve' ? 'success' : 'warning');
        setList(prev => prev.filter(r => r.id !== req.id));
    };

    return (
        <>
            <Header />
            <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, py: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <IconButton variant="plain" onClick={() => navigate('/manager')}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Inventory2RoundedIcon sx={{ color: 'var(--joy-palette-primary-400)' }} />
                    <Typography level="h3" sx={{ fontWeight: 800, flex: 1 }}>Заявки на списание</Typography>
                    <Chip variant="soft" color="warning" sx={{ fontWeight: 800 }}>{list.length}</Chip>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                ) : list.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', color: 'var(--joy-palette-text-tertiary)', py: 8 }}>
                        Нет заявок на согласование
                    </Typography>
                ) : (
                    <Stack spacing={1.2}>
                        {list.map(req => (
                            <Box key={req.id} sx={{
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                borderRadius: 'var(--joy-radius-xl)', p: 2,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontWeight: 800, flex: 1 }}>{req.comment}</Typography>
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                        {fmtDateTime(req.created_at)}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 1 }}>
                                    {req.requested_by_name || '—'}{req.location_name ? ` · ${req.location_name}` : ''}
                                </Typography>
                                <Stack spacing={0.4} sx={{ mb: 1.5 }}>
                                    {(req.items || []).map(it => (
                                        <Box key={it.id} sx={{
                                            display: 'flex', justifyContent: 'space-between',
                                            px: 1.2, py: 0.6, borderRadius: 'var(--joy-radius-md)',
                                            bgcolor: 'var(--joy-palette-background-level2)',
                                        }}>
                                            <Typography sx={{ fontSize: 13 }}>{it.product_name_snapshot}</Typography>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'var(--joy-palette-danger-400)' }}>
                                                −{fmtQty(it.quantity)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <Button fullWidth variant="soft" color="danger" loading={busyId === req.id}
                                        onClick={() => review(req, 'reject')} sx={{ fontWeight: 700 }}>
                                        Отклонить
                                    </Button>
                                    <Button fullWidth variant="solid" color="success" loading={busyId === req.id}
                                        onClick={() => review(req, 'approve')} sx={{ fontWeight: 800 }}>
                                        Одобрить и списать
                                    </Button>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </>
    );
}